import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../app/store';
import { useExportFileMutation } from '../../../services/boreholeApi';
import { usePaperSizesQuery } from '../../../services/enumApi';
import { useTrackMutation } from '../../../services/pdfApi';
import { useTrackTypesHierarchyQuery } from '../../../services/trackTypeApi';
import {
  setDepthMajorIntervals,
  setDepthMinorIntervals,
  setDepthUnit,
  setShowDepthGridlines,
  setShowDepthMinorIntervals,
  setShowValueAxisAnnotation
} from '../templateSlice';
import getConfig from '../../../app/config';
import auth from '../../../components/auth';
import { getAccountHubConnection } from '../../../services/accountHubConnection';
import fetchProgress from 'fetch-progress';

/**
 * Custom hook to manage all left-side panel state and handlers
 * Keeps BoreholeLogViewerV2 cleaner by centralizing panel logic
 */
type UseBoreholeLogPanelsArgs = {
  setStartDepth: (value: number | null) => void;
  setEndDepth: (value: number | null) => void;
  startDepth: number | null;
  endDepth: number | null;
  depthUnit: number | null;
  setDepthType: (value: string) => void;
  clientId?: string | null;
  projectId?: string | null;
  boreholeId?: string | null;
  selectedTemplateId?: string | null;
  selectedViewId?: string | null;
  depthType?: string;
  isViewMode?: boolean;
  isMultiBoreholeMode?: boolean;
  connection?: any;
  connected?: boolean;
  trackTypesHierarchyData?: any;
};

export const useBoreholeLogPanels = ({
  setStartDepth,
  setEndDepth,
  startDepth,
  endDepth,
  depthUnit,
  setDepthType,
  clientId,
  projectId,
  boreholeId,
  selectedTemplateId,
  selectedViewId,
  depthType,
  isViewMode,
  isMultiBoreholeMode,
  connection,
  connected,
  trackTypesHierarchyData: trackTypesHierarchyDataProp
}: UseBoreholeLogPanelsArgs) => {
  const dispatch = useDispatch<AppDispatch>();
  const [exportFileMutation] = useExportFileMutation();
  const [createPdf] = useTrackMutation();
  // Panel visibility states
  const [showLeftSidePanel, setShowLeftSidePanel] = useState<boolean>(true);
  const [showDepthPanel, setShowDepthPanel] = useState<boolean>(false);
  const [showXAxisPanel, setShowXAxisPanel] = useState<boolean>(false);
  const [showPrintPanel, setShowPrintPanel] = useState<boolean>(false);
  const [showTemplatesPanel, setShowTemplatesPanel] = useState<boolean>(true);
  const [showViewsPanel, setShowViewsPanel] = useState<boolean>(false);
  const [showInfoPanel, setShowInfoPanel] = useState<boolean>(false);
  const [showExportPanel, setShowExportPanel] = useState<boolean>(false);

  // Print state
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [printProgress, setPrintProgress] = useState<any>(null);
  const [printError, setPrintError] = useState<any>(null);

  // Export state
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<any>(null);
  const [exportFileName, setExportFileName] = useState<string>('export.csv');
  const [exportCompleted, setExportCompleted] = useState<boolean>(false);
  const [exportUrl, setExportUrl] = useState<string>('');
  const [exportError, setExportError] = useState<any>(null);
  const exportUrlRef = useRef<string | null>(null);

  // Reset all left panels
  const resetLeftPanels = useCallback(() => {
    setShowDepthPanel(false);
    setShowXAxisPanel(false);
    setShowPrintPanel(false);
    setShowTemplatesPanel(false);
    setShowViewsPanel(false);
    setShowInfoPanel(false);
    setShowExportPanel(false);
  }, []);

  // Panel toggle handlers
  const onShowDepthPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowDepthPanel(true);
  }, [resetLeftPanels]);

  const onShowXAxisPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowXAxisPanel(true);
  }, [resetLeftPanels]);

  const onShowPrintPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowPrintPanel(true);
  }, [resetLeftPanels]);

  const onShowTemplatesPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowTemplatesPanel(true);
  }, [resetLeftPanels]);

  const onShowViewsPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowViewsPanel(true);
  }, [resetLeftPanels]);

  const onShowInfoPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowInfoPanel(true);
  }, [resetLeftPanels]);

  const onShowExportPanel = useCallback(() => {
    setShowLeftSidePanel(true);
    resetLeftPanels();
    setShowExportPanel(true);
  }, [resetLeftPanels]);

  const onToggleLeftSidePanel = useCallback(() => {
    setShowLeftSidePanel(prev => !prev);
  }, []);

  // Auto-switch from templates panel to views panel when entering multi-borehole mode
  // This matches V1 behavior
  useEffect(() => {
    if (isMultiBoreholeMode && showTemplatesPanel) {
      onShowViewsPanel();
    }
  }, [isMultiBoreholeMode, showTemplatesPanel, onShowViewsPanel]);

  // Depth panel handlers
  const onChangeDepthType = useCallback((value: any) => {
    const nextValue = value?.target?.value ?? value;
    if (typeof nextValue === 'string') {
      setDepthType(nextValue);
    }
  }, [setDepthType]);

  const onChangeDepthUnit = useCallback((unit: number) => {
    if (unit == null) return;
    // Convert the visible depth range synchronously before dispatching the unit change.
    // Doing this here (inside the event handler) ensures both state updates are batched
    // in the same React render, preventing async useEffect races from applying the
    // conversion a second time.
    const FEET_PER_METER = 3.2808;
    if (depthUnit != null && depthUnit !== unit && startDepth != null && endDepth != null) {
      const toFeet = unit === 2;
      const convert = (d: number) => toFeet ? d * FEET_PER_METER : d / FEET_PER_METER;
      setStartDepth(convert(startDepth));
      setEndDepth(convert(endDepth));
    }
    dispatch(setDepthUnit(unit));
  }, [dispatch, depthUnit, startDepth, endDepth, setStartDepth, setEndDepth]);

  const setSkipLoad = useCallback((_skip: boolean) => {
    // Skip load flag for depth panel
  }, []);

  const onSetDepthMajorIntervals = useCallback((intervals: number) => {
    if (intervals == null) return;
    dispatch(setDepthMajorIntervals(intervals));
  }, [dispatch]);

  const onSetDepthMinorIntervals = useCallback((intervals: number) => {
    if (intervals == null) return;
    dispatch(setDepthMinorIntervals(intervals));
  }, [dispatch]);

  const onSetShowDepthGridlines = useCallback((show: boolean) => {
    dispatch(setShowDepthGridlines(show));
  }, [dispatch]);

  const onSetShowDepthMinorIntervals = useCallback((show: boolean) => {
    dispatch(setShowDepthMinorIntervals(show));
  }, [dispatch]);

  // X-axis panel handlers
  const onSetShowValueAxisAnnotation = useCallback((show: boolean) => {
    dispatch(setShowValueAxisAnnotation(show));
  }, [dispatch]);

  // Print panel handler
  const handleOkPrintModal = useCallback((
    size: number,
    orientation: number,
    pagesToPrintOver: number,
    pixelWidth: number | null,
    pixelHeight: number | null,
    fanFold: boolean,
    tracksPerPage: number,
    fixTracksPerPage: boolean,
    totalPages: number,
    fixTotalPages: boolean,
    startDepth: number,
    endDepth: number,
    depthPerPage: number,
    printOverPages: boolean,
    leftDepthScale: boolean,
    rightDepthScale: boolean,
    printStatisticalSummary: boolean,
    selectedDataForDepthRange: number[]
  ) => {
    if (!clientId || !projectId || !boreholeId) return;
    const conn = getAccountHubConnection();
    if (!conn?.connectionId) return;
    setIsPrinting(true);
    setPrintError(null);
    setPrintProgress({ progress: null, message: 'Pending', failed: false });

    createPdf({
      template: {
        connectionId: conn.connectionId,
        clientId,
        projectId,
        boreholeId,
        templateId: isViewMode ? null : selectedTemplateId,
        viewId: isViewMode ? selectedViewId : null,
        startDepth,
        endDepth,
        size,
        orientation,
        byBoreholeDepth: depthType === 'Borehole',
        pagesToPrintOver,
        pixelWidth,
        pixelHeight,
        fanFold,
        tracksPerPage,
        fixTracksPerPage,
        totalPages,
        fixTotalPages,
        depthPerPage,
        printOverPages,
        leftDepthScale,
        rightDepthScale,
        printStatisticalSummary,
        selectedDataForDepthRange
      }
    })
      .unwrap?.()
      .catch((error: any) => {
        setIsPrinting(false);
        const firstKey = error?.data?.errors ? Object.keys(error.data.errors)[0] : null;
        const firstMessage = firstKey ? error.data.errors[firstKey]?.[0] : null;
        setPrintError(firstMessage || 'An error occurred while creating the PDF.');
      });
  }, [clientId, projectId, boreholeId, createPdf, depthType, isViewMode, selectedTemplateId, selectedViewId]);

  // Export panel handler
  const onExportFile = useCallback((
    headerType: number,
    deliminator: string,
    templateId: number,
    dataExportType: number,
    lithologyTrackType?: number,
    perMeter?: boolean
  ) => {
    if (!clientId || !projectId || !boreholeId) return;
    const conn = getAccountHubConnection();
    if (!conn?.connectionId) return;
    setIsExporting(true);
    setExportCompleted(false);
    setExportProgress({ progress: null, message: 'Pending', failed: false });
    setExportError(null);

    exportFileMutation({
      clientId,
      projectId,
      boreholeId,
      templateId: templateId || selectedTemplateId,
      exportFileName,
      headerType,
      deliminator,
      orderByBoreholeDepth: depthType === 'Borehole',
      connectionId: conn.connectionId,
      dataExportType,
      lithologyTrackType,
      perMeter
    });
  }, [clientId, projectId, boreholeId, exportFileMutation, exportFileName, selectedTemplateId, depthType]);

  const apiUrl = getConfig().apiUrl;

  const downloadFile = useCallback((
    url: any,
    fileName: any,
    fileType: number,
    updateProgress: (percentage: number) => void,
    completed: () => void
  ) => {
    if (!clientId || !projectId || !boreholeId) return '';
    const fileUrl = `${apiUrl}client/${clientId}/project/${projectId}/borehole/${boreholeId}/download?fileName=${encodeURIComponent(url)}&fileType=${fileType}&access_token=${auth.getTokenForImages()}`;
    fetch(fileUrl, {
      method: 'GET',
      headers: new Headers({
        Authorization: 'Bearer ' + auth.getTokenForImages()
      })
    })
      .then(
        fetchProgress({
          onProgress(progress: any) {
            updateProgress(progress.percentage);
          },
          onError(err) {
            console.log(err);
          }
        })
      )
      .then((response: any) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(new Blob([blob]));
        const link: any = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        completed();
      })
      .catch((response) => {
        console.log(response);
      });
    return fileUrl;
  }, [apiUrl, clientId, projectId, boreholeId]);

  useEffect(() => {
    const connection = getAccountHubConnection();
    if (!connection || !connected || connection?.state !== 'Connected') return;

    connection.off('GenerateTrackPdfComplete');
    connection.off('SendCreateTrackPdfProgress');
    connection.off('SendExportDataProgress');
    connection.off('SendDownloadExport');

    connection.on('GenerateTrackPdfComplete', (url: any, fileName: any) => {
      const mininmumPercentage = 90;
      const maxinmumPercentage = 100;
      downloadFile(url, fileName, 5, (percentage: number) => {
        const progressPercentage = mininmumPercentage + ((maxinmumPercentage - mininmumPercentage) * percentage / 100);
        setPrintProgress({ progress: progressPercentage, message: 'Downloading...', failed: false });
      }, () => setIsPrinting(false));
    });

    connection.on('SendCreateTrackPdfProgress', (progress: number, message: string, failed: boolean) => {
      setPrintProgress({ progress, message, failed });
      if (failed) {
        setPrintError(message);
        setIsPrinting(false);
      }
    });

    connection.on('SendExportDataProgress', (progress: number, message: string, failed: boolean) => {
      setExportProgress({ progress, message, failed });
    });

    connection.on('SendDownloadExport', (url: any, fileName: any) => {
      const mininmumPercentage = 90;
      const maxinmumPercentage = 100;
      setExportFileName(fileName);
      const fileUrl = downloadFile(url, fileName, 6, (percentage: number) => {
        const progressPercentage = mininmumPercentage + ((maxinmumPercentage - mininmumPercentage) * percentage / 100);
        setExportProgress({ progress: progressPercentage, message: 'Completed exporting. Downloading...', failed: false });
      }, () => {
        setIsExporting(false);
        setExportCompleted(true);
      });
      setExportUrl(fileUrl);
    });
  }, [connected, downloadFile]);

  // Depth setter wrappers for type safety
  const onSetStartDepth = useCallback((value: number | null | undefined) => {
    setStartDepth(value ?? null);
  }, [setStartDepth]);

  const onSetEndDepth = useCallback((value: number | null | undefined) => {
    setEndDepth(value ?? null);
  }, [setStartDepth]);

  // Memoized data
  const { data: paperSizes } = usePaperSizesQuery();

  const { data: trackTypesHierarchyDataFetched } = useTrackTypesHierarchyQuery(
    { boreholeId: parseInt(boreholeId!, 10), clientId: clientId!, projectId: projectId! },
    { refetchOnMountOrArgChange: true, skip: !boreholeId || !clientId || !projectId || trackTypesHierarchyDataProp != null }
  );
  const trackTypesHierarchyData = trackTypesHierarchyDataProp ?? trackTypesHierarchyDataFetched;

  const dataForDepthRangeOptions = useMemo(() => {
    if (!trackTypesHierarchyData) return [] as any[];
    const containsAssayData = trackTypesHierarchyData.trackTypeCategories
      .filter((c: any) => c.id === 1035)
      .reduce((acc: any, cur: any) => [...acc, ...cur.trackTypes.filter((t: any) => !t.noData)], [])
      .length > 0;
    const options = trackTypesHierarchyData.trackTypeCategories
      .filter((c: any) => c.id === 1033 || c.id === 1038)
      .reduce((acc: any, cur: any) => [...acc, ...cur.trackTypes.filter((t: any) => !t.noData)], [])
      .map((t: any) => ({ value: t.id, name: t.name }));
    if (containsAssayData) {
      options.push({ value: -99, name: 'Assay' });
    }
    return options;
  }, [trackTypesHierarchyData]);

  return {
    // Panel visibility states
    showLeftSidePanel,
    setShowLeftSidePanel,
    showDepthPanel,
    showXAxisPanel,
    showPrintPanel,
    showTemplatesPanel,
    showViewsPanel,
    showInfoPanel,
    showExportPanel,

    // Panel toggle handlers
    onShowDepthPanel,
    onShowXAxisPanel,
    onShowPrintPanel,
    onShowTemplatesPanel,
    onShowViewsPanel,
    onShowInfoPanel,
    onShowExportPanel,
    onToggleLeftSidePanel,

    // Depth panel handlers
    onChangeDepthType,
    onChangeDepthUnit,
    setSkipLoad,
    onSetDepthMajorIntervals,
    onSetDepthMinorIntervals,
    onSetShowDepthGridlines,
    onSetShowDepthMinorIntervals,
    onSetStartDepth,
    onSetEndDepth,

    // X-axis panel handler
    onSetShowValueAxisAnnotation,

    // Print panel
    isPrinting,
    setIsPrinting,
    printProgress,
    setPrintProgress,
    printError,
    setPrintError,
    handleOkPrintModal,

    // Export panel
    isExporting,
    setIsExporting,
    exportProgress,
    setExportProgress,
    exportFileName,
    setExportFileName,
    exportCompleted,
    setExportCompleted,
    exportUrl,
    setExportUrl,
    exportError,
    setExportError,
    onExportFile,

    // Memoized data
    paperSizes,
    dataForDepthRangeOptions
  };
};
